import { CLINICAL_PATHS, isClinicalAccountUser } from '../helpers/clinical_account';
import { isPhoneCallsEnabled } from '../helpers/feature_checker';
import { redirectAfterSurgery } from '../helpers/redirect_after_surgery_helper.js';

export const needsPhoneVerification = (user, { impersonating = false } = {}) => {
  if (impersonating || !user) return false;
  if (user.current_profile_type !== 'Patient') return false;
  if (user.phone_verified) return false;

  return isPhoneCallsEnabled(user.account);
};

export const getPublicOnlyRedirectUrl = (user, options = {}) => {
  if (user?.current_profile_type === 'SuperUser') {
    return '/member/admin-session';
  }

  if (needsPhoneVerification(user, options)) {
    return '/member/verify-phone';
  }

  if (isClinicalAccountUser(user)) {
    return CLINICAL_PATHS.programInsights;
  }

  if (user?.current_profile_type === 'AccountAdmin') {
    return '/provider/insights';
  }

  return '/member/conversations';
};

export const getUserRedirectUrl = (user, history) => {
  const { current_profile_type, is_reactiv8, matched_with_mentor, current_profile } = user;

  if (isClinicalAccountUser(user)) {
    return CLINICAL_PATHS.programInsights;
  }

  // Route configurations for each profile type
  const routeConfig = {
    AccountAdmin: () => (is_reactiv8 ? '/provider/mentor-connect' : '/provider/insights'),

    Patient: () => {
      const match = current_profile?.mentor_match;
      const isMatchedWithMentor = matched_with_mentor === true;

      if (!isMatchedWithMentor) {
        // This will handle the redirect to the questionnaire
        redirectAfterSurgery(history, match, user.account);
        return null;
      }
      return '/member/conversations';
    },

    PatientPartner: () => '/member/conversations',

    // Default fallback for any other profile types
    default: () => '/member/conversations',
  };

  const getRoute = routeConfig[current_profile_type] || routeConfig.default;
  return typeof getRoute === 'function' ? getRoute() : getRoute;
};

export const handleUserRedirect = (user, history, options = {}) => {
  const { isImpersonation = false, returnTo = '' } = options;

  // Handle first-time PatientPartner sign-in (password change required) - but not for impersonation
  if (!isImpersonation && user.current_profile_type === 'PatientPartner' && user.sign_in_count === 1) {
    const url = returnTo
      ? `/member/change-password?return_to=${encodeURIComponent(returnTo)}`
      : '/member/change-password';
    history.push(url);
    return;
  }

  if (!isImpersonation && needsPhoneVerification(user)) {
    history.push('/member/verify-phone');
    return;
  }

  // If there's a specific return URL, use it (but not for impersonation)
  if (!isImpersonation && returnTo !== '') {
    history.push(returnTo);
    return;
  }

  if (user.current_profile_type === 'SuperUser') {
    window.location.href = '/admin';
    return;
  }

  // Otherwise, determine redirect based on user profile
  const redirectUrl = getUserRedirectUrl(user, history);

  // Only push to history if we got a URL back (some handlers like redirectAfterSurgery manage their own redirects)
  if (redirectUrl) {
    window.location.href = redirectUrl;
  }
};
